import { z } from "zod";

export const maxEbookPages = 3_000;

export const ebookStatuses = [
  "draft",
  "uploading",
  "queued",
  "processing",
  "ready",
  "failed",
] as const;

export const EbookStatusSchema = z.enum(ebookStatuses);
export type EbookStatus = z.infer<typeof EbookStatusSchema>;

export const ebookFonts = [
  "Prompt",
  "Sarabun",
  "Kanit",
  "Mitr",
  "K2D",
  "Athiti",
  "Pridi",
] as const;
export const EbookFontSchema = z.enum(ebookFonts);
export type EbookFont = z.infer<typeof EbookFontSchema>;

export const ebookDisplayModes = ["single", "double"] as const;
export const EbookDisplayModeSchema = z.enum(ebookDisplayModes);
export type EbookDisplayMode = z.infer<typeof EbookDisplayModeSchema>;

export const ReaderControlsSchema = z.object({
  showMenu: z.boolean(),
  showSearch: z.boolean(),
  showPdfDownload: z.boolean(),
  showPagination: z.boolean(),
  showZoom: z.boolean(),
  showFullscreen: z.boolean(),
});
export type ReaderControls = z.infer<typeof ReaderControlsSchema>;

export const defaultReaderControls: ReaderControls = {
  showMenu: true,
  showSearch: true,
  showPdfDownload: true,
  showPagination: true,
  showZoom: true,
  showFullscreen: true,
};

const pdfDownloadNamePattern = /^[\p{L}\p{M}\p{N} ]+$/u;

export const PdfDownloadNameSchema = z.string()
  .trim()
  .min(1)
  .max(30)
  .regex(pdfDownloadNamePattern, "Use letters, marks, numbers, and spaces only");
export type PdfDownloadName = z.infer<typeof PdfDownloadNameSchema>;

export const PdfDownloadNameInputSchema = z.string()
  .trim()
  .max(30)
  .refine(
    (value) => value === "" || pdfDownloadNamePattern.test(value),
    "Use letters, marks, numbers, and spaces only",
  )
  .transform((value) => value === "" ? null : value);

export const PlanIdSchema = z.enum(["free", "starter", "pro", "business", "enterprise"]);
export type PlanId = z.infer<typeof PlanIdSchema>;

export const paidPlanIds = ["starter", "pro", "business", "enterprise"] as const;
export const PaidPlanIdSchema = z.enum(paidPlanIds);
export type PaidPlanId = z.infer<typeof PaidPlanIdSchema>;

export const planCatalog = {
  free: { books: 1, pagesPerBook: 20 },
  starter: { books: 3, pagesPerBook: 80 },
  pro: { books: 50, pagesPerBook: maxEbookPages },
  business: { books: null, pagesPerBook: maxEbookPages },
  enterprise: { books: null, pagesPerBook: maxEbookPages },
} as const satisfies Record<PlanId, { books: number | null; pagesPerBook: number }>;

export const UserRoleSchema = z.enum(["user", "admin"]);
export type UserRole = z.infer<typeof UserRoleSchema>;
export const PlanStatusSchema = z.enum(["unselected", "free", "trial", "active", "expired"]);
export type PlanStatus = z.infer<typeof PlanStatusSchema>;

export const UpdatePlanRequestSchema = z.object({
  plan: PlanIdSchema,
});
export type UpdatePlanRequest = z.infer<typeof UpdatePlanRequestSchema>;

export const UserSchema = z.object({
  id: z.string().uuid(),
  googleSubject: z.string(),
  email: z.string().email(),
  displayName: z.string().max(120),
  photoUrl: z.string().url().nullable(),
  emailVerified: z.boolean(),
  plan: PlanIdSchema.nullable(),
  role: UserRoleSchema,
  planStatus: PlanStatusSchema,
  planExpiresAt: z.string().datetime().nullable(),
  trialExpiresAt: z.string().datetime().nullable(),
});
export type User = z.infer<typeof UserSchema>;

export const PlanEntitlementSchema = z.object({
  effectivePlan: PlanIdSchema.nullable(),
  status: PlanStatusSchema,
  expiresAt: z.string().datetime().nullable(),
  trialExpiresAt: z.string().datetime().nullable(),
  booksUsed: z.number().int().nonnegative(),
  booksLimit: z.number().int().positive().nullable(),
  pagesPerBook: z.number().int().positive(),
});
export type PlanEntitlement = z.infer<typeof PlanEntitlementSchema>;

export const PaymentRequestStatusSchema = z.enum([
  "uploading", "pending", "approved", "rejected", "cancelled",
]);
export type PaymentRequestStatus = z.infer<typeof PaymentRequestStatusSchema>;
export const PaymentRequestSchema = z.object({
  id: z.string().uuid(),
  requestedPlan: PaidPlanIdSchema,
  status: PaymentRequestStatusSchema,
  trialExpiresAt: z.string().datetime().nullable(),
  reviewedAt: z.string().datetime().nullable(),
  rejectionReason: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type PaymentRequest = z.infer<typeof PaymentRequestSchema>;

export const PaymentConfigSchema = z.object({
  bankName: z.string(),
  accountName: z.string(),
  accountNumber: z.string(),
});
export type PaymentConfig = z.infer<typeof PaymentConfigSchema>;

export const BillingOverviewSchema = z.object({
  entitlement: PlanEntitlementSchema,
  currentRequest: PaymentRequestSchema.nullable(),
  config: PaymentConfigSchema,
});
export type BillingOverview = z.infer<typeof BillingOverviewSchema>;

export const StartPaymentRequestSchema = z.object({
  plan: PaidPlanIdSchema,
  fileName: z.string().trim().min(1).max(255),
  contentType: z.enum(["image/png", "image/jpeg", "image/webp", "application/pdf"]),
  size: z.number().int().positive().max(5 * 1024 * 1024),
});
export const CompletePaymentRequestSchema = z.object({ objectKey: z.string().min(1).max(1024) });
export const PaymentUploadStartResponseSchema = z.object({
  request: PaymentRequestSchema,
  objectKey: z.string(),
  assetWorkerUrl: z.string().url(),
  grant: z.string(),
  uploadUrl: z.string().optional(),
  expiresAt: z.string().datetime(),
});
export type PaymentUploadStartResponse = z.infer<typeof PaymentUploadStartResponseSchema>;

export const AdminUpdateUserPlanSchema = z.object({
  plan: PlanIdSchema,
  expiresAt: z.string().datetime().nullable(),
});
export const RejectPaymentRequestSchema = z.object({
  reason: z.string().trim().min(1).max(1000),
});
export const AdminUserSchema = z.object({
  id: z.string().uuid(), email: z.string().email(), displayName: z.string(), role: UserRoleSchema,
  entitlement: PlanEntitlementSchema,
});
export type AdminUser = z.infer<typeof AdminUserSchema>;
export const AdminPaymentSchema = PaymentRequestSchema.extend({
  userId: z.string().uuid(), userEmail: z.string().email(), userDisplayName: z.string(),
});
export type AdminPayment = z.infer<typeof AdminPaymentSchema>;

export const EbookSchema = z.object({
  id: z.string().uuid(),
  title: z.string().min(1).max(180),
  description: z.string().max(1000),
  copyrightText: z.string().max(300),
  pdfDownloadName: PdfDownloadNameSchema.nullable(),
  status: EbookStatusSchema,
  pageCount: z.number().int().nonnegative(),
  processedPages: z.number().int().nonnegative(),
  storageBytes: z.number().int().nonnegative(),
  thumbnailUrl: z.string().url().nullable(),
  faviconUrl: z.string().url().nullable(),
  logoUrl: z.string().url().nullable(),
  backgroundImageUrl: z.string().url().nullable(),
  coverImageUrl: z.string().url().nullable(),
  backgroundColor: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  fontFamily: EbookFontSchema,
  displayMode: EbookDisplayModeSchema,
  showTocLinks: z.boolean(),
  readerControls: ReaderControlsSchema,
  processingError: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Ebook = z.infer<typeof EbookSchema>;

export const storageAssetKinds = [
  "original_pdf",
  "page_image",
  "thumbnail",
  "favicon",
  "logo",
  "background",
  "cover",
  "media_audio",
  "media_video",
  "published_pdf",
] as const;
export const StorageAssetKindSchema = z.enum(storageAssetKinds);
export type StorageAssetKind = z.infer<typeof StorageAssetKindSchema>;

export const EbookStorageBreakdownItemSchema = z.object({
  kind: StorageAssetKindSchema,
  bytes: z.number().int().nonnegative(),
  count: z.number().int().nonnegative(),
});
export type EbookStorageBreakdownItem = z.infer<typeof EbookStorageBreakdownItemSchema>;

export const EbookStorageSchema = z.object({
  ebookId: z.string().uuid(),
  totalBytes: z.number().int().nonnegative(),
  breakdown: z.array(EbookStorageBreakdownItemSchema),
});
export type EbookStorage = z.infer<typeof EbookStorageSchema>;

export const EbookPageSchema = z.object({
  id: z.string().uuid(),
  pageNumber: z.number().int().positive(),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  imageUrl: z.string().url(),
  thumbnailUrl: z.string().url(),
  hasText: z.boolean(),
  textEdited: z.boolean(),
  textRevision: z.number().int().positive(),
  textTruncated: z.boolean(),
  textSource: z.enum(["pdf", "ocr"]),
  textQuality: z.number().int().min(0).max(100).nullable(),
  ocrAttempted: z.boolean(),
  ocrConfidence: z.number().min(0).max(100).nullable(),
  thaiNormalized: z.boolean(),
  spellSuggestionCount: z.number().int().nonnegative(),
});
export type EbookPage = z.infer<typeof EbookPageSchema>;

export type ReaderNavigationItem = {
  title: string;
  pageNumber: number;
  children: ReaderNavigationItem[];
};
export const ReaderNavigationItemSchema: z.ZodType<ReaderNavigationItem> = z.lazy(
  () => z.object({
    title: z.string().trim().min(1).max(500),
    pageNumber: z.number().int().positive(),
    children: z.array(ReaderNavigationItemSchema),
  }),
);

export const TableOfContentsSchema = z.object({
  items: z.array(ReaderNavigationItemSchema).max(500),
  source: z.enum(["pdf", "ocr", "manual"]),
  revision: z.number().int().positive(),
  updatedAt: z.string().datetime().nullable(),
});
export type TableOfContents = z.infer<typeof TableOfContentsSchema>;

export const TocHotspotRectSchema = z.object({
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  width: z.number().positive().max(1),
  height: z.number().positive().max(1),
}).refine((rect) => rect.x + rect.width <= 1.0001 && rect.y + rect.height <= 1.0001, {
  message: "Hotspot rectangle must stay within the page",
});
export type TocHotspotRect = z.infer<typeof TocHotspotRectSchema>;

export const TocHotspotSchema = z.object({
  id: z.string().uuid(),
  tocPageNumber: z.number().int().positive().max(maxEbookPages),
  title: z.string().trim().min(1).max(500),
  targetPageNumber: z.number().int().positive().max(maxEbookPages),
  rect: TocHotspotRectSchema,
  source: z.enum(["pdf_annotation", "pdf_text", "ocr"]),
  confidence: z.number().min(0).max(100),
  enabled: z.boolean(),
});
export type TocHotspot = z.infer<typeof TocHotspotSchema>;

export const TocHotspotPageSchema = z.object({
  pageNumber: z.number().int().positive().max(maxEbookPages),
  imageUrl: z.string().url(),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
});

export const TocHotspotsSchema = z.object({
  enabled: z.boolean(),
  items: z.array(TocHotspotSchema).max(500),
  pages: z.array(TocHotspotPageSchema).max(100),
  revision: z.number().int().positive(),
  updatedAt: z.string().datetime().nullable(),
});
export type TocHotspots = z.infer<typeof TocHotspotsSchema>;

export const UpdateTocHotspotsRequestSchema = z.object({
  enabled: z.boolean(),
  items: z.array(TocHotspotSchema).max(500),
  expectedRevision: z.number().int().positive(),
});

export const TocHotspotManifestSchema = z.object({
  version: z.literal(1),
  items: z.array(TocHotspotSchema).max(500),
});
export type TocHotspotManifest = z.infer<typeof TocHotspotManifestSchema>;

export const mediaOverlayKinds = [
  "youtube",
  "audio_url",
  "video_url",
  "audio_asset",
  "video_asset",
  "page_link",
] as const;
export const MediaOverlayKindSchema = z.enum(mediaOverlayKinds);
export type MediaOverlayKind = z.infer<typeof MediaOverlayKindSchema>;

export const mediaOverlayAnimationTypes = [
  "none",
  "fade",
  "fade-up",
  "slide-left",
  "slide-right",
  "zoom-in",
] as const;
export const MediaOverlayAnimationTypeSchema = z.enum(mediaOverlayAnimationTypes);
export type MediaOverlayAnimationType = z.infer<typeof MediaOverlayAnimationTypeSchema>;

export const MediaOverlayRectSchema = z.object({
  left: z.number().min(0).max(100),
  top: z.number().min(0).max(100),
  width: z.number().positive().max(100),
  height: z.number().positive().max(100),
}).refine((rect) => rect.left + rect.width <= 100.0001 && rect.top + rect.height <= 100.0001, {
  message: "Media overlay rectangle must stay within the page",
});
export type MediaOverlayRect = z.infer<typeof MediaOverlayRectSchema>;

function isYoutubeUrl(value: string): boolean {
  try {
    const url = new URL(value);
    const host = url.hostname.toLowerCase().replace(/^www\./, "");
    return (
      (host === "youtube.com" && (url.pathname === "/watch" || url.pathname.startsWith("/embed/")))
      || host === "youtu.be"
    );
  } catch {
    return false;
  }
}

const mediaOverlayBaseSchema = z.object({
  id: z.string().uuid(),
  pageNumber: z.number().int().positive().max(maxEbookPages),
  kind: MediaOverlayKindSchema,
  sourceUrl: z.string().url().max(2048).nullable(),
  assetKey: z.string().min(1).max(1024).nullable(),
  assetUrl: z.string().url().nullable(),
  contentType: z.string().max(120).nullable(),
  sizeBytes: z.number().int().nonnegative().nullable(),
  targetPageNumber: z.number().int().positive().max(maxEbookPages).nullable(),
  rect: MediaOverlayRectSchema,
  zIndex: z.number().int().min(0).max(999),
  animationType: MediaOverlayAnimationTypeSchema,
  animationDelayMs: z.number().int().min(0).max(60_000),
  animationDurationMs: z.number().int().min(0).max(30_000),
  enabled: z.boolean(),
});

export const MediaOverlaySchema = mediaOverlayBaseSchema.superRefine((item, context) => {
  if (item.kind === "youtube") {
    if (!item.sourceUrl || !isYoutubeUrl(item.sourceUrl)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sourceUrl"],
        message: "YouTube overlays require a valid YouTube URL",
      });
    }
    if (item.assetKey) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["assetKey"],
        message: "YouTube overlays must not include an uploaded asset",
      });
    }
    if (item.targetPageNumber !== null) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["targetPageNumber"],
        message: "YouTube overlays must not include a target page",
      });
    }
  } else if (item.kind === "audio_url" || item.kind === "video_url") {
    if (!item.sourceUrl) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sourceUrl"],
        message: "URL media overlays require a source URL",
      });
    }
    if (item.assetKey) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["assetKey"],
        message: "URL media overlays must not include an uploaded asset",
      });
    }
    if (item.targetPageNumber !== null) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["targetPageNumber"],
        message: "Media URL overlays must not include a target page",
      });
    }
  } else if (item.kind === "page_link") {
    if (item.targetPageNumber === null) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["targetPageNumber"],
        message: "Page link overlays require a target page",
      });
    }
    if (item.sourceUrl || item.assetKey || item.assetUrl || item.contentType || item.sizeBytes !== null) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["kind"],
        message: "Page link overlays must not include media source fields",
      });
    }
  } else {
    if (!item.assetKey) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["assetKey"],
        message: "Uploaded media overlays require an asset key",
      });
    }
    if (item.sourceUrl || item.targetPageNumber !== null) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["kind"],
        message: "Uploaded media overlays must not include URL or target page fields",
      });
    }
  }
});
export type MediaOverlay = z.infer<typeof MediaOverlaySchema>;

export const MediaOverlayInputSchema = mediaOverlayBaseSchema.omit({
  id: true,
  pageNumber: true,
  assetUrl: true,
  contentType: true,
  sizeBytes: true,
}).extend({
  id: z.string().uuid().optional(),
}).superRefine((item, context) => {
  if (item.kind === "youtube") {
    if (!item.sourceUrl || !isYoutubeUrl(item.sourceUrl)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sourceUrl"],
        message: "YouTube overlays require a valid YouTube URL",
      });
    }
    if (item.assetKey) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["assetKey"],
        message: "YouTube overlays must not include an uploaded asset",
      });
    }
    if (item.targetPageNumber !== null) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["targetPageNumber"],
        message: "YouTube overlays must not include a target page",
      });
    }
  } else if (item.kind === "audio_url" || item.kind === "video_url") {
    if (!item.sourceUrl) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sourceUrl"],
        message: "URL media overlays require a source URL",
      });
    }
    if (item.assetKey) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["assetKey"],
        message: "URL media overlays must not include an uploaded asset",
      });
    }
    if (item.targetPageNumber !== null) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["targetPageNumber"],
        message: "Media URL overlays must not include a target page",
      });
    }
  } else if (item.kind === "page_link") {
    if (item.targetPageNumber === null) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["targetPageNumber"],
        message: "Page link overlays require a target page",
      });
    }
    if (item.sourceUrl || item.assetKey) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["kind"],
        message: "Page link overlays must not include media source fields",
      });
    }
  } else {
    if (!item.assetKey) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["assetKey"],
        message: "Uploaded media overlays require an asset key",
      });
    }
    if (item.sourceUrl || item.targetPageNumber !== null) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["kind"],
        message: "Uploaded media overlays must not include URL or target page fields",
      });
    }
  }
});
export type MediaOverlayInput = z.infer<typeof MediaOverlayInputSchema>;

export const MediaOverlaysSchema = z.object({
  pageNumber: z.number().int().positive().max(maxEbookPages),
  revision: z.number().int().positive(),
  updatedAt: z.string().datetime().nullable(),
  items: z.array(MediaOverlaySchema).max(100),
});
export type MediaOverlays = z.infer<typeof MediaOverlaysSchema>;

export const UpdateMediaOverlaysRequestSchema = z.object({
  expectedRevision: z.number().int().positive(),
  items: z.array(MediaOverlayInputSchema).max(100),
});

export const StartMediaOverlayUploadRequestSchema = z.object({
  kind: z.enum(["audio_asset", "video_asset"]),
  fileName: z.string().min(1).max(255),
  contentType: z.enum([
    "audio/mpeg",
    "audio/mp4",
    "audio/wav",
    "audio/ogg",
    "video/mp4",
    "video/webm",
    "video/ogg",
  ]),
  size: z.number().int().positive().max(100 * 1024 * 1024),
}).superRefine((value, context) => {
  const audioTypes = new Set(["audio/mpeg", "audio/mp4", "audio/wav", "audio/ogg"]);
  const videoTypes = new Set(["video/mp4", "video/webm", "video/ogg"]);
  if (value.kind === "audio_asset" && (!audioTypes.has(value.contentType) || value.size > 50 * 1024 * 1024)) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ["contentType"], message: "Audio upload type or size is not allowed" });
  }
  if (value.kind === "video_asset" && (!videoTypes.has(value.contentType) || value.size > 100 * 1024 * 1024)) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ["contentType"], message: "Video upload type or size is not allowed" });
  }
});

export const CompleteMediaOverlayUploadRequestSchema = z.object({
  kind: z.enum(["audio_asset", "video_asset"]),
  objectKey: z.string().min(1).max(1024),
});

export type MediaOverlayUploadStartResponse = {
  objectKey: string;
  assetWorkerUrl: string;
  grant: string;
  uploadUrl?: string;
  expiresAt: string;
};

export const MediaOverlayRuntimeItemSchema = z.object({
  id: z.string().uuid(),
  pageNumber: z.number().int().positive().max(maxEbookPages),
  kind: z.enum(["youtube", "audio", "video", "link"]),
  src: z.string().min(1).max(2048).nullable(),
  targetPageNumber: z.number().int().positive().max(maxEbookPages).nullable(),
  rect: MediaOverlayRectSchema,
  zIndex: z.number().int().min(0).max(999),
  animationType: MediaOverlayAnimationTypeSchema,
  animationDelayMs: z.number().int().min(0).max(60_000),
  animationDurationMs: z.number().int().min(0).max(30_000),
  enabled: z.boolean(),
});
export type MediaOverlayRuntimeItem = z.infer<typeof MediaOverlayRuntimeItemSchema>;

export const MediaOverlayManifestSchema = z.object({
  version: z.literal(1),
  items: z.array(MediaOverlayRuntimeItemSchema).max(100),
});
export type MediaOverlayManifest = z.infer<typeof MediaOverlayManifestSchema>;

export const TocHotspotDetectionSchema = z.object({
  status: z.enum(["idle", "queued", "processing", "ready", "failed"]),
  stage: z.enum(["idle", "queued", "downloading", "detecting", "saving", "complete"]),
  progress: z.number().int().min(0).max(100),
  totalPages: z.number().int().nonnegative(),
  processedPages: z.number().int().nonnegative(),
  error: z.string().nullable(),
  errorCode: z.string().nullable().optional(),
  requestedAt: z.string().datetime().nullable(),
  finishedAt: z.string().datetime().nullable(),
  candidatePages: z.number().int().nonnegative(),
  detectedItems: z.number().int().nonnegative(),
  resultCode: z.enum(["detected", "no_toc_page", "no_positioned_text", "no_unique_target"]).nullable(),
});
export type TocHotspotDetection = z.infer<typeof TocHotspotDetectionSchema>;

export const UpdateTableOfContentsRequestSchema = z.object({
  items: z.array(ReaderNavigationItemSchema).max(500).nullable(),
  expectedRevision: z.number().int().positive(),
});

function navigationItemCount(items: ReaderNavigationItem[]): number {
  return items.reduce(
    (total, item) => total + 1 + navigationItemCount(item.children),
    0,
  );
}

export function assertNavigationLimit(items: ReaderNavigationItem[]): void {
  if (navigationItemCount(items) > 500) {
    throw new Error("Table of contents cannot contain more than 500 items");
  }
}

const headingPrefix =
  /^(?:บทที่\s*\d+|บทนำ|คำนำ|สารบัญ|บทสรุป|ภาคผนวก|ส่วนที่\s*\d+|chapter\s+\d+|introduction|preface|contents|conclusion|appendix|section\s+\d+)/iu;
const numberedHeading = /^(?:\d+\.\d+(?:\.\d+){0,2}|[IVXLCDM]+[.)])\s+\S/iu;
const contentsHeading = /^(?:สารบัญ(?:\s*\(ต่อ\))?|table of contents|contents)$/iu;

function compactHeading(value: string): string {
  return value
    .normalize("NFC")
    .replace(/\s+/gu, " ")
    .trim();
}

function headingKey(value: string): string {
  return compactHeading(value)
    .toLocaleLowerCase()
    .replace(/[\p{P}\p{S}\p{Z}]+/gu, "");
}

function sectionDepth(title: string): number {
  if (/^(?:บทที่\s*\d+|chapter\s+\d+)/iu.test(title)) return 0;
  const section = title.match(/^(\d+(?:\.\d+){1,3})\s+/u)?.[1];
  return section ? Math.min(section.split(".").length - 1, 3) : 0;
}

function chapterHeadingPages(
  chapter: string,
  pages: Array<{ pageNumber: number; text: string }>,
  contentsPages: Set<number>,
): number[] {
  return pages.filter((page) =>
    !contentsPages.has(page.pageNumber)
    && page.text.split(/\r?\n/).map(compactHeading).some((line) =>
      new RegExp(`^(?:บทที่|chapter)\\s*${chapter}(?:\\s|$)`, "iu").test(line)
    )
  ).map((page) => page.pageNumber);
}

function targetPageForHeading(
  title: string,
  pages: Array<{ pageNumber: number; text: string }>,
  contentsPages: Set<number>,
): number | null {
  const section = title.match(/^(\d+(?:\.\d+){1,3})\s+/u)?.[1];
  const key = headingKey(title);
  const chapter = title.match(/^(?:บทที่|chapter)\s*(\d+)/iu)?.[1];
  const frontMatter = /^(?:บทสรุปผู้บริหาร|Executive Summary|คำนำ|รายการอ้างอิง)$/iu
    .test(title);
  const prefixLength = frontMatter ? 8 : 36;
  const prefix = Array.from(key).slice(0, prefixLength).join("");
  for (const page of pages) {
    if (contentsPages.has(page.pageNumber)) continue;
    const lines = page.text.split(/\r?\n/).map(compactHeading).filter(Boolean);
    if (
      chapter
      && lines.some((line) =>
        new RegExp(`^(?:บทที่|chapter)\\s*${chapter}(?:\\s|$)`, "iu").test(line)
      )
    ) {
      return page.pageNumber;
    }
    if (
      section
      && lines.some((line) => {
        if (!new RegExp(
          `^${section.replace(/\./g, "\\.")}(?:\\s|$)`,
          "u",
        ).test(line)) {
          return false;
        }
        const lineKey = headingKey(line);
        const comparisonLength = Math.min(14, key.length, lineKey.length);
        return comparisonLength >= 4
          && key.slice(0, comparisonLength) === lineKey.slice(0, comparisonLength);
      })
    ) {
      return page.pageNumber;
    }
    if (
      prefix.length >= 6
      && lines.some((line) => headingKey(line).includes(prefix))
    ) {
      return page.pageNumber;
    }
  }
  return null;
}

function tableOfContentsSuggestions(
  pages: Array<{ pageNumber: number; text: string }>,
): ReaderNavigationItem[] | null {
  const tocPages = pages.filter((page) =>
    page.text.split(/\r?\n/).map(compactHeading).filter(Boolean)
      .slice(0, 3).some((line) => contentsHeading.test(line))
  );
  if (tocPages.length === 0) return null;
  const tocPageNumbers = new Set(pages.filter((page) =>
    page.text.split(/\r?\n/).map(compactHeading).filter(Boolean)
      .slice(0, 3).some((line) => /^สารบัญ/iu.test(line) || contentsHeading.test(line))
  ).map((page) => page.pageNumber));
  const candidates: Array<{
    title: string;
    pageNumber: number;
    depth: number;
  }> = [];
  const seen = new Set<string>();
  for (const page of tocPages) {
    for (const rawLine of page.text.split(/\r?\n/)) {
      const title = compactHeading(rawLine).replace(/\s+\d{1,3}$/u, "").trim();
      if (
        !title
        || contentsHeading.test(title)
        || /^\|.*\|$/u.test(title)
        || /^[\p{N}\p{P}\p{S}\p{Z}]+$/u.test(title)
        || Array.from(title).length > 160
      ) {
        continue;
      }
      const isHeading =
        headingPrefix.test(title)
        || numberedHeading.test(title)
        || /^(?:Executive Summary|รายการอ้างอิง)$/iu.test(title);
      if (!isHeading) continue;
      const key = headingKey(title);
      if (seen.has(key)) continue;
      const targetPage = targetPageForHeading(title, pages, tocPageNumbers);
      if (targetPage === null) continue;
      seen.add(key);
      candidates.push({
        title,
        pageNumber: targetPage,
        depth: sectionDepth(title),
      });
      if (candidates.length >= 100) break;
    }
  }
  const roots: ReaderNavigationItem[] = [];
  const sections = new Map<string, ReaderNavigationItem>();
  const chapters = new Map<string, ReaderNavigationItem>();
  for (const candidate of candidates) {
    const item: ReaderNavigationItem = {
      title: candidate.title,
      pageNumber: candidate.pageNumber,
      children: [],
    };
    const chapter = candidate.title.match(
      /^(?:บทที่|chapter)\s*(\d+)/iu,
    )?.[1];
    const section = candidate.title.match(
      /^(\d+(?:\.\d+){1,3})\s+/u,
    )?.[1];
    if (chapter) {
      roots.push(item);
      chapters.set(chapter, item);
      continue;
    }
    if (section) {
      const parts = section.split(".");
      const parentSection = parts.slice(0, -1).join(".");
      const parent = sections.get(parentSection) ?? chapters.get(parts[0]!);
      if (parent) parent.children.push(item);
      else roots.push(item);
      sections.set(section, item);
      continue;
    }
    roots.push(item);
  }
  const descendantMinimum = (items: ReaderNavigationItem[]): number =>
    items.reduce(
      (minimum, item) => Math.min(
        minimum,
        item.pageNumber,
        descendantMinimum(item.children),
      ),
      Number.POSITIVE_INFINITY,
    );
  for (const root of roots) {
    const chapter = root.title.match(/^(?:บทที่|chapter)\s*(\d+)/iu)?.[1];
    if (!chapter) continue;
    const ceiling = descendantMinimum(root.children);
    const matches = chapterHeadingPages(chapter, pages, tocPageNumbers)
      .filter((pageNumber) => pageNumber <= ceiling);
    const clusters: number[][] = [];
    for (const pageNumber of matches) {
      const latest = clusters[clusters.length - 1];
      if (latest?.[latest.length - 1] === pageNumber - 1) latest.push(pageNumber);
      else clusters.push([pageNumber]);
    }
    const bestCluster = Number.isFinite(ceiling)
      ? clusters[clusters.length - 1]
      : clusters[0];
    if (bestCluster?.[0]) root.pageNumber = bestCluster[0];
  }
  return roots;
}

export function suggestTableOfContents(
  pages: Array<{ pageNumber: number; text: string }>,
): ReaderNavigationItem[] {
  const detectedContents = tableOfContentsSuggestions(pages);
  if (detectedContents?.length) return detectedContents;
  const suggestions: ReaderNavigationItem[] = [];
  const seen = new Set<string>();
  for (const page of pages) {
    const lines = page.text.replace(/\r/g, "\n").split("\n");
    for (let index = 0; index < Math.min(lines.length, 10); index += 1) {
      const title = compactHeading(lines[index] ?? "");
      const characters = Array.from(title);
      if (characters.length < 2 || characters.length > 120) continue;
      if (/^[\p{N}\p{P}\p{S}\p{Z}]+$/u.test(title) || /[.!?。！？]$/u.test(title)) {
        continue;
      }
      const explicit = headingPrefix.test(title) || numberedHeading.test(title);
      const uppercaseEnglish =
        /[A-Z]/u.test(title)
        && !/[a-z]/u.test(title)
        && characters.length <= 80;
      const surroundedBySpace =
        (index === 0 || !(lines[index - 1] ?? "").trim())
        && !(lines[index + 1] ?? "").trim();
      const likelyCoverTitle = page.pageNumber === 1 && index < 3;
      if (!explicit && !uppercaseEnglish && !surroundedBySpace && !likelyCoverTitle) {
        continue;
      }
      const key = title.normalize("NFC").toLocaleLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      suggestions.push({ title, pageNumber: page.pageNumber, children: [] });
      if (suggestions.length >= 100) return suggestions;
    }
  }
  return suggestions;
}

export const EbookRuntimeConfigSchema = z.object({
  title: z.string().min(1).max(180),
  description: z.string().max(1000),
  copyrightText: z.string().max(300),
  pageCount: z.number().int().positive().max(maxEbookPages),
  pageRatio: z.number().positive(),
  pageExtension: z.enum(["png", "webp", "jpg"]),
  backgroundColor: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  fontFamily: EbookFontSchema,
  displayMode: EbookDisplayModeSchema,
  backgroundImageUrl: z.string().nullable(),
  logoUrl: z.string().nullable(),
  faviconUrl: z.string().nullable(),
  coverImageUrl: z.string().nullable(),
  pdfUrl: z.string().nullable(),
  pdfDownloadName: PdfDownloadNameSchema.nullable(),
  pageUrlTemplate: z.string().nullable(),
  thumbnailUrlTemplate: z.string().nullable(),
  navigationUrl: z.string().nullable(),
  searchManifestUrl: z.string().nullable(),
  mediaOverlayUrlTemplate: z.string().nullable(),
  shareUrl: z.string().nullable(),
  qrCodeUrl: z.string().nullable(),
  readerControls: ReaderControlsSchema,
  flipSoundUrl: z.string().nullable(),
});
export type EbookRuntimeConfig = z.infer<typeof EbookRuntimeConfigSchema>;

export const EbookPageTextSchema = z.object({
  pageNumber: z.number().int().positive(),
  extractedText: z.string(),
  editedText: z.string().nullable(),
  effectiveText: z.string(),
  revision: z.number().int().positive(),
  truncated: z.boolean(),
  textSource: z.enum(["pdf", "ocr"]),
  textQuality: z.number().int().min(0).max(100).nullable(),
  ocrAttempted: z.boolean(),
  ocrConfidence: z.number().min(0).max(100).nullable(),
  ocrReasons: z.array(z.string()),
  rawOcrText: z.string().nullable(),
  normalizedText: z.string().nullable(),
  normalizationSummary: z.object({
    changed: z.boolean(),
    rawLength: z.number().int().nonnegative(),
    normalizedLength: z.number().int().nonnegative(),
  }).nullable(),
  spellSuggestions: z.array(z.object({
    word: z.string(),
    suggestions: z.array(z.string()).max(5),
  })).max(100),
  updatedAt: z.string().datetime().nullable(),
});
export type EbookPageText = z.infer<typeof EbookPageTextSchema>;

export const UpdateEbookPageTextRequestSchema = z.object({
  editedText: z.string().max(1_000_000).nullable(),
  expectedRevision: z.number().int().positive(),
});

export const UpdateReaderControlsRequestSchema = ReaderControlsSchema;

export const contentExtractionStatuses = [
  "idle",
  "queued",
  "processing",
  "ready",
  "failed",
] as const;
export const ContentExtractionStatusSchema = z.enum(contentExtractionStatuses);
export const contentExtractionStages = [
  "idle",
  "queued",
  "downloading",
  "extracting_text",
  "ocr",
  "extracting_outline",
  "saving",
  "complete",
] as const;
export const ContentExtractionStageSchema = z.enum(contentExtractionStages);
export type ContentExtractionStage = z.infer<typeof ContentExtractionStageSchema>;
export const ContentExtractionSchema = z.object({
  status: ContentExtractionStatusSchema,
  stage: ContentExtractionStageSchema,
  progress: z.number().int().min(0).max(100),
  totalPages: z.number().int().nonnegative(),
  processedPages: z.number().int().nonnegative(),
  ocrPagesTotal: z.number().int().nonnegative(),
  ocrPagesProcessed: z.number().int().nonnegative(),
  error: z.string().nullable(),
  requestedAt: z.string().datetime().nullable(),
  finishedAt: z.string().datetime().nullable(),
});
export type ContentExtraction = z.infer<typeof ContentExtractionSchema>;

export const EbookPageSearchItemSchema = z.object({
  pageNumber: z.number().int().positive(),
  snippet: z.string(),
  matchCount: z.number().int().positive(),
  thumbnailUrl: z.string().url(),
});
export type EbookPageSearchItem = z.infer<typeof EbookPageSearchItemSchema>;

export const EbookPageSearchResponseSchema = z.object({
  items: z.array(EbookPageSearchItemSchema),
  total: z.number().int().nonnegative(),
});
export type EbookPageSearchResponse = z.infer<typeof EbookPageSearchResponseSchema>;

export const ReaderSearchPageSchema = z.object({
  pageNumber: z.number().int().positive(),
  text: z.string(),
});
export type ReaderSearchPage = z.infer<typeof ReaderSearchPageSchema>;

export const ReaderSearchChunkSchema = z.object({
  pages: z.array(ReaderSearchPageSchema),
});

export const ReaderSearchManifestSchema = z.object({
  version: z.literal(1),
  totalPages: z.number().int().nonnegative(),
  chunks: z.array(z.object({
    url: z.string(),
    fromPage: z.number().int().positive(),
    toPage: z.number().int().positive(),
  })),
});
export type ReaderSearchManifest = z.infer<typeof ReaderSearchManifestSchema>;

export const ReaderNavigationSchema = z.object({
  version: z.literal(1),
  items: z.array(ReaderNavigationItemSchema),
});

export type ReaderSearchFile = {
  name: string;
  json: string;
};

export function buildReaderSearchFiles(
  pages: ReaderSearchPage[],
  targetBytes = 1024 * 1024,
): { manifest: ReaderSearchManifest; files: ReaderSearchFile[] } {
  const encoder = new TextEncoder();
  const files: ReaderSearchFile[] = [];
  let chunk: ReaderSearchPage[] = [];
  const flush = () => {
    if (chunk.length === 0) return;
    const name = `chunk-${String(files.length + 1).padStart(4, "0")}.json`;
    files.push({
      name,
      json: JSON.stringify({ pages: chunk }),
    });
    chunk = [];
  };
  const fileBytes = (entries: ReaderSearchPage[]) =>
    encoder.encode(JSON.stringify({ pages: entries })).byteLength;
  const fragments: ReaderSearchPage[] = [];
  for (const page of pages) {
    if (fileBytes([page]) <= targetBytes || page.text.length === 0) {
      fragments.push(page);
      continue;
    }
    let start = 0;
    while (start < page.text.length) {
      let low = start + 1;
      let high = page.text.length;
      let end = low;
      while (low <= high) {
        const middle = Math.floor((low + high) / 2);
        const candidate = { pageNumber: page.pageNumber, text: page.text.slice(start, middle) };
        if (fileBytes([candidate]) <= targetBytes) {
          end = middle;
          low = middle + 1;
        } else {
          high = middle - 1;
        }
      }
      if (end < page.text.length) {
        const code = page.text.charCodeAt(end - 1);
        if (code >= 0xD800 && code <= 0xDBFF) end -= 1;
      }
      if (end <= start) {
        throw new Error("Search chunk target is too small for its JSON envelope");
      }
      fragments.push({ pageNumber: page.pageNumber, text: page.text.slice(start, end) });
      if (end >= page.text.length) break;
      const overlap = Math.min(99, Math.floor((end - start) / 4));
      start = Math.max(start + 1, end - overlap);
    }
  }
  for (const fragment of fragments) {
    const candidate = [...chunk, fragment];
    if (chunk.length > 0 && fileBytes(candidate) > targetBytes) flush();
    chunk.push(fragment);
  }
  flush();
  return {
    manifest: {
      version: 1,
      totalPages: pages.length,
      chunks: files.map((file, index) => {
        const filePages = JSON.parse(file.json) as { pages: ReaderSearchPage[] };
        return {
          url: `./${file.name}`,
          fromPage: filePages.pages[0]?.pageNumber ?? index + 1,
          toPage: filePages.pages[filePages.pages.length - 1]?.pageNumber ?? index + 1,
        };
      }),
    },
    files,
  };
}

export const PaginationSchema = z.object({
  page: z.number().int().positive(),
  pageSize: z.number().int().min(1).max(100),
  total: z.number().int().nonnegative(),
});

export const EbookListResponseSchema = PaginationSchema.extend({
  items: z.array(EbookSchema),
});
export type EbookListResponse = z.infer<typeof EbookListResponseSchema>;

export const EbookPagesResponseSchema = PaginationSchema.extend({
  items: z.array(EbookPageSchema),
});
export type EbookPagesResponse = z.infer<typeof EbookPagesResponseSchema>;

export const pagesProjectNameSuffix = "-ebook";
const pagesProjectNameMaxLength = 58;
const PagesProjectNameBaseSchema = z
  .string()
  .trim()
  .min(1)
  .max(pagesProjectNameMaxLength)
  .regex(
    /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/,
    "Use lowercase letters, numbers, and hyphens; do not start or end with a hyphen",
  );

export function normalizeEbookPagesProjectName(value: string): string {
  const name = value.trim();
  if (!name || name.endsWith(pagesProjectNameSuffix)) return name;
  const maxBaseLength = pagesProjectNameMaxLength - pagesProjectNameSuffix.length;
  const base = name.slice(0, maxBaseLength).replace(/-+$/, "");
  return `${base}${pagesProjectNameSuffix}`;
}

export function toEbookPublishKey(value: string): string {
  const base = value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+/, "")
    .replace(/-+/g, "-")
    .slice(0, pagesProjectNameMaxLength)
    .replace(/-+$/, "");
  return normalizeEbookPagesProjectName(base);
}

export const PagesProjectNameSchema = PagesProjectNameBaseSchema
  .transform(normalizeEbookPagesProjectName)
  .pipe(PagesProjectNameBaseSchema.refine(
    (value) => value.endsWith(pagesProjectNameSuffix),
    "Project name must end with -ebook",
  ));

export const CreateEbookRequestSchema = z.object({
  title: z.string().trim().min(1).max(180),
});
export type CreateEbookRequest = z.infer<typeof CreateEbookRequestSchema>;

export const UpdateEbookRequestSchema = z.object({
  title: z.string().trim().min(1).max(180).optional(),
  description: z.string().trim().max(1000).optional(),
  copyrightText: z.string().trim().max(300).optional(),
  pdfDownloadName: PdfDownloadNameInputSchema.nullish(),
  backgroundColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
  fontFamily: EbookFontSchema.optional(),
  displayMode: EbookDisplayModeSchema.optional(),
}).refine((value) => (
  value.title !== undefined
  || value.description !== undefined
  || value.copyrightText !== undefined
  || value.pdfDownloadName !== undefined
  || value.backgroundColor !== undefined
  || value.fontFamily !== undefined
  || value.displayMode !== undefined
), {
  message: "At least one field is required",
});
export type UpdateEbookRequest = z.infer<typeof UpdateEbookRequestSchema>;

export const brandingKinds = ["favicon", "logo", "background", "cover"] as const;
export const BrandingKindSchema = z.enum(brandingKinds);
export type BrandingKind = z.infer<typeof BrandingKindSchema>;

export const BrandingUploadRequestSchema = z.object({
  fileName: z.string().min(1).max(255),
  contentType: z.enum([
    "image/png",
    "image/jpeg",
    "image/webp",
    "image/x-icon",
    "image/vnd.microsoft.icon",
  ]),
  size: z.number().int().positive().max(15 * 1024 * 1024),
});

export const CompleteBrandingUploadRequestSchema = z.object({
  objectKey: z.string().min(1).max(1024),
});

export type BrandingUploadStartResponse = {
  objectKey: string;
  assetWorkerUrl: string;
  grant: string;
  uploadUrl?: string;
  expiresAt: string;
};

export const ProfileUpdateRequestSchema = z.object({
  displayName: z.string().trim().min(1).max(120),
});

export const openAiModels = [
  "gpt-5.4-mini",
  "gpt-5.6-luna",
  "gpt-5.6-terra",
  "gpt-4o-mini",
] as const;
export const OpenAiModelSchema = z.enum(openAiModels);
export type OpenAiModel = z.infer<typeof OpenAiModelSchema>;

export const SettingsSchema = z.object({
  locale: z.enum(["th", "en"]),
  theme: z.enum(["light", "dark", "system"]),
  openAiConfigured: z.boolean(),
  openAiKeyHint: z.string().nullable(),
  openAiModel: OpenAiModelSchema,
});
export type Settings = z.infer<typeof SettingsSchema>;

export const UpdateSettingsRequestSchema = z.object({
  locale: z.enum(["th", "en"]),
  theme: z.enum(["light", "dark", "system"]),
  openAiModel: OpenAiModelSchema,
  openAiApiKey: z.string().trim().min(20).max(512).optional(),
  clearOpenAiApiKey: z.boolean().optional().default(false),
}).refine(
  (value) => !(value.openAiApiKey && value.clearOpenAiApiKey),
  { message: "Cannot set and clear the OpenAI API key together" },
);

export const AiReviewScopeSchema = z.enum([
  "current_page",
  "selected_pages",
  "low_quality_pages",
  "not_ai_reviewed_pages",
  "unedited_pages",
  "all_pages",
]);
export type AiReviewScope = z.infer<typeof AiReviewScopeSchema>;

export const StartAiReviewRequestSchema = z.object({
  scope: AiReviewScopeSchema,
  pageNumber: z.number().int().positive().max(maxEbookPages).optional(),
  pageNumbers: z.array(z.number().int().positive().max(maxEbookPages)).max(maxEbookPages).optional(),
}).superRefine((value, context) => {
  if (value.scope === "current_page" && value.pageNumber === undefined) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["pageNumber"],
      message: "pageNumber is required for current_page",
    });
  }
  if (value.scope === "selected_pages" && (!value.pageNumbers || value.pageNumbers.length === 0)) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["pageNumbers"],
      message: "pageNumbers is required for selected_pages",
    });
  }
});

export const AiReviewChangeSchema = z.object({
  before: z.string().max(500),
  after: z.string().max(500),
  reason: z.string().max(500),
});
export type AiReviewChange = z.infer<typeof AiReviewChangeSchema>;

export const AiReviewPageSchema = z.object({
  pageNumber: z.number().int().positive(),
  status: z.enum(["queued", "processing", "ready", "failed"]),
  inputRevision: z.number().int().positive(),
  originalText: z.string(),
  correctedText: z.string().nullable(),
  summary: z.string().nullable(),
  changes: z.array(AiReviewChangeSchema).max(100),
  error: z.string().nullable(),
  appliedAt: z.string().datetime().nullable(),
});
export type AiReviewPage = z.infer<typeof AiReviewPageSchema>;

export const AiReviewSchema = z.object({
  jobId: z.string().uuid().nullable(),
  status: z.enum(["idle", "queued", "processing", "ready", "failed"]),
  scope: AiReviewScopeSchema.nullable(),
  model: OpenAiModelSchema.nullable(),
  totalPages: z.number().int().nonnegative(),
  processedPages: z.number().int().nonnegative(),
  pages: z.array(AiReviewPageSchema),
  error: z.string().nullable(),
  requestedAt: z.string().datetime().nullable(),
  finishedAt: z.string().datetime().nullable(),
});
export type AiReview = z.infer<typeof AiReviewSchema>;

export const ApplyAiReviewRequestSchema = z.object({
  jobId: z.string().uuid(),
  expectedRevision: z.number().int().positive(),
});

export const publicationStatuses = [
  "draft",
  "queued",
  "publishing",
  "published",
  "outdated",
  "failed",
] as const;
export const PublicationStatusSchema = z.enum(publicationStatuses);
export type PublicationStatus = z.infer<typeof PublicationStatusSchema>;

export const publicationProgressStages = [
  "idle",
  "queued",
  "preparing",
  "copying",
  "building",
  "packaging",
  "uploading",
  "verifying",
  "complete",
  "failed",
] as const;
export const PublicationProgressStageSchema = z.enum(publicationProgressStages);
export type PublicationProgressStage = z.infer<typeof PublicationProgressStageSchema>;

export const UpdatePublicationRequestSchema = z.object({
  projectName: PagesProjectNameSchema,
});

export const EbookPublicationSchema = z.object({
  projectName: PagesProjectNameSchema.nullable(),
  projectNameLocked: z.boolean(),
  status: PublicationStatusSchema,
  progressStage: PublicationProgressStageSchema,
  progressPercent: z.number().int().min(0).max(100),
  deploymentUrl: z.string().url().nullable(),
  originDeploymentUrl: z.string().url().nullable(),
  deploymentId: z.string().nullable(),
  releaseId: z.string().uuid().nullable(),
  publishedSourceId: z.string().uuid().nullable(),
  lastError: z.string().nullable(),
  publishedAt: z.string().datetime().nullable(),
  updatedAt: z.string().datetime().nullable(),
});
export type EbookPublication = z.infer<typeof EbookPublicationSchema>;

export const DashboardSchema = z.object({
  total: z.number().int().nonnegative(),
  ready: z.number().int().nonnegative(),
  processing: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  storageBytes: z.number().int().nonnegative(),
});
export type Dashboard = z.infer<typeof DashboardSchema>;

export const ProcessingSchema = z.object({
  status: EbookStatusSchema,
  processedPages: z.number().int().nonnegative(),
  pageCount: z.number().int().nonnegative(),
  progress: z.number().min(0).max(100),
  error: z.string().nullable(),
});
export type Processing = z.infer<typeof ProcessingSchema>;

export const StartUploadRequestSchema = z.object({
  fileName: z.string().min(1).max(255),
  contentType: z.literal("application/pdf"),
  size: z.number().int().positive().max(250 * 1024 * 1024),
  sha256: z.string().regex(/^[a-f0-9]{64}$/i),
  autoOcrScan: z.boolean().default(true),
});

export const CompleteUploadRequestSchema = z.object({
  workerUploadId: z.string().min(1),
  parts: z
    .array(
      z.object({
        partNumber: z.number().int().min(1).max(10_000),
        etag: z.string().min(1),
      }),
    )
    .min(1)
    .max(10_000),
});

export const UploadGrantPayloadSchema = z.object({
  sub: z.string().uuid(),
  ebookId: z.string().uuid(),
  uploadId: z.string().uuid(),
  key: z.string().min(1).max(1024),
  op: z.enum(["multipart:create", "multipart:part", "multipart:complete", "multipart:abort"]),
  contentType: z.literal("application/pdf"),
  maxSize: z.number().int().positive(),
  exp: z.number().int().positive(),
});
export type UploadGrantPayload = z.infer<typeof UploadGrantPayloadSchema>;

export const AssetGrantPayloadSchema = z.object({
  key: z.string().min(1).max(1024),
  op: z.enum(["get", "put", "delete", "head"]),
  contentType: z.string().max(120).optional(),
  maxSize: z.number().int().positive().optional(),
  exp: z.number().int().positive(),
});
export type AssetGrantPayload = z.infer<typeof AssetGrantPayloadSchema>;

export const PublishedAssetGrantPayloadSchema = z.object({
  sourceKey: z.string().min(1).max(1024).optional(),
  destinationKey: z.string().min(1).max(1024),
  op: z.enum([
    "published:copy",
    "published:put",
    "published:multipart:create",
    "published:multipart:part",
    "published:multipart:complete",
    "published:multipart:abort",
    "published:delete",
    "published:delete_prefix",
    "published:list_prefix",
  ]),
  contentType: z.string().max(120).optional(),
  maxSize: z.number().int().positive().optional(),
  exp: z.number().int().positive(),
}).superRefine((value, context) => {
  if (value.op === "published:copy" && !value.sourceKey) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["sourceKey"],
      message: "sourceKey is required for published:copy",
    });
  }
  if (
    (
      value.op === "published:put"
      || value.op === "published:multipart:create"
      || value.op === "published:multipart:part"
      || value.op === "published:multipart:complete"
      || value.op === "published:multipart:abort"
    )
    && !value.contentType
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["contentType"],
      message: "contentType is required for published uploads",
    });
  }
});
export type PublishedAssetGrantPayload = z.infer<typeof PublishedAssetGrantPayloadSchema>;

export const PreviewGrantPayloadSchema = z.object({
  ebookId: z.string().uuid(),
  ownerUserId: z.string().uuid(),
  op: z.literal("preview"),
  exp: z.number().int().positive(),
});
export type PreviewGrantPayload = z.infer<typeof PreviewGrantPayloadSchema>;

export type PreviewTokenResponse = {
  token: string;
  expiresAt: string;
};

export const ApiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    fieldErrors: z.record(z.array(z.string())).optional(),
    requestId: z.string(),
  }),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;

export type UploadStartResponse = {
  uploadId: string;
  sourceId: string;
  objectKey: string;
  assetWorkerUrl: string;
  uploadUrl?: string;
  abortUrl?: string;
  grants: {
    create: string;
    part: string;
    complete: string;
    abort: string;
  };
  expiresAt: string;
  partSize: number;
};
